import type React from 'react';

import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { type IdNameParticipantsInterface, type PairedParticipantInterface } from 'components/overlays/doubles/types';
import PairedPlayersListItem from 'components/panels/doubles/PairedPlayersListItem';
import { useStyles } from 'components/panels/doubles/styles';

interface PairedPlayersPanelProps {
  pairedParticipants?: PairedParticipantInterface[];
  setPairedParticipants: (pairedParticipants: (pairedParticipants) => PairedParticipantInterface[]) => void;
  setUnpairedParticipants: (unpairedParticipants: (unpairedParticipants) => IdNameParticipantsInterface[]) => void;
}

const PairedPlayersPanel: React.FC<React.PropsWithChildren<PairedPlayersPanelProps>> = ({
  pairedParticipants,
  setPairedParticipants,
  setUnpairedParticipants,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleUnpairParticipants = (participant: PairedParticipantInterface) => {
    setPairedParticipants((pairedParticipants) => {
      const pairedOrEmpty = pairedParticipants || [];
      return pairedOrEmpty.filter(
        (pairedParticipant) =>
          pairedParticipant.individualParticipantsIds[0] !== participant.individualParticipantsIds[0],
      );
    });
    setUnpairedParticipants((unpairedParticipants) => {
      const toRemove = [participant.individualParticipants[0]];
      if (participant.individualParticipants[1]) {
        toRemove.push(participant.individualParticipants[1]);
      }
      return [...toRemove, ...unpairedParticipants];
    });
  };

  return (
    <Grid container direction="column">
      <Grid item>
        <Typography className={classes.titleTypography} variant="h1">
          {t('pairedDoublesTeams')} ({pairedParticipants?.length || 0})
        </Typography>
      </Grid>
      <Grid item className={classes.panelWrapper}>
        {pairedParticipants?.length && pairedParticipants.length > 0 ? (
          <List aria-label="secondary mailbox folders" component="nav">
            {pairedParticipants?.map((participant) => (
              <PairedPlayersListItem
                key={participant.individualParticipantsIds[0]}
                participant={participant}
                onUnpairParticipant={handleUnpairParticipants}
              />
            ))}
          </List>
        ) : (
          <Grid item className={classes.emptyListWrapper}>
            <div className={classes.emptyListTextWrapperWithoutPadding}>
              <Typography>{t('pairedPlayersPanel.listWrapper')}</Typography>
            </div>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default PairedPlayersPanel;
